import React, { useContext, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Divider, Progress } from '@mantine/core';
import { AlertCircle } from 'tabler-icons-react';
import ResponsiveModal from '../../common/ResponsiveModal';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppText from '../../common/AppText';
import AppCheckbox from '../../common/AppCheckbox';
import { Context as VendorContext } from '../../../providers/VendorContextProvider';
import { triggerNotification } from '../../../helpers/notification';
import { singularPluralFormat } from '../../../helpers/format';

const INITIAL_FORM_STATE = {
  includePrintImages: false,
  includeProductImages: true,
  isRunning: false,
  hasRun: false,
  completedCount: 0,
  batch: [],
  succeeded: [],
  failed: []
};

const RegenerateDesignImagesModal = ({
  isOpen,
  onClose,
  onSuccess,
  fkEcomVendor,
  products
}) => {
  const { regenerateVendorProductDesignImages } = useContext(VendorContext);
  const isCancelledRef = useRef(false);
  const [formState, setFormState] = useState(INITIAL_FORM_STATE);

  // Products the next run will touch. After a partial failure the retry only
  // revisits the ones that failed, so a long run never has to be repeated in full.
  const pendingProducts =
    formState.hasRun && !formState.isRunning && formState.failed.length > 0
      ? products.filter((p) =>
          formState.failed.some(
            (f) => f.pkEcomVendorProduct === p.pkEcomVendorProduct
          )
        )
      : products;

  // The batch is frozen when a run starts so progress can't shift under itself as
  // failures accumulate.
  const activeBatch = formState.hasRun ? formState.batch : pendingProducts;

  useEffect(() => {
    if (isOpen) {
      isCancelledRef.current = false;
      setFormState(INITIAL_FORM_STATE);
    }
    else {
      isCancelledRef.current = true;
    }
  }, [isOpen]);

  // Stop an in-flight run if the view is navigated away from.
  useEffect(
    () => () => {
      isCancelledRef.current = true;
    },
    []
  );

  const regenerateProduct = (product, regenerateOptions) =>
    new Promise((resolve) => {
      regenerateVendorProductDesignImages(
        fkEcomVendor,
        [
          {
            pkEcomVendorProduct: product.pkEcomVendorProduct,
            ...regenerateOptions
          }
        ],
        () => resolve({ product, error: null }),
        (error, type, data) =>
          resolve({
            product,
            // The shared handler only reads the API's message on a 409, but image
            // generation reports why it failed on a 500 - prefer that detail.
            error:
              typeof data?.error === 'string' && data.error
                ? data.error
                : error ?? 'Unknown error'
          })
      );
    });

  const onStart = async () => {
    const regenerateOptions = {
      includePrintImages: formState.includePrintImages,
      includeProductImages: formState.includeProductImages
    };
    const batch = pendingProducts;

    isCancelledRef.current = false;
    setFormState((c) => ({
      ...c,
      isRunning: true,
      hasRun: true,
      completedCount: 0,
      batch,
      succeeded: [],
      failed: []
    }));

    const succeeded = [];
    const failed = [];

    // Deliberately sequential: each product is its own request, so a long design
    // can never exceed the API request timeout and every failure is attributable
    // to a single product.
    for (let i = 0; i < batch.length; i += 1) {
      if (isCancelledRef.current) {
        break;
      }

      // eslint-disable-next-line no-await-in-loop
      const { product, error } = await regenerateProduct(
        batch[i],
        regenerateOptions
      );

      if (error) {
        failed.push({ ...product, error });
      }
      else {
        succeeded.push(product);
      }

      if (isCancelledRef.current) {
        break;
      }

      setFormState((c) => ({
        ...c,
        completedCount: i + 1,
        succeeded: [...succeeded],
        failed: [...failed]
      }));
    }

    if (isCancelledRef.current) {
      return;
    }

    setFormState((c) => ({ ...c, isRunning: false }));

    if (succeeded.length > 0 && onSuccess) {
      onSuccess();
    }

    if (failed.length === 0) {
      triggerNotification(
        `Images regenerated for ${singularPluralFormat(
          succeeded.length,
          'product',
          'products'
        )}.`,
        'Success',
        'green'
      );
      onClose();
    }
  };

  const onCancelRun = () => {
    isCancelledRef.current = true;
    setFormState((c) => ({ ...c, isRunning: false }));
    if (formState.succeeded.length > 0 && onSuccess) {
      onSuccess();
    }
  };

  const progressPercent =
    activeBatch.length === 0
      ? 0
      : (formState.completedCount / activeBatch.length) * 100;
  const isFinishedWithFailures =
    formState.hasRun && !formState.isRunning && formState.failed.length > 0;
  const currentProductName = activeBatch[formState.completedCount]?.name ?? '';

  return (
    <ResponsiveModal
      formSectionProps={{
        onCancel: formState.isRunning ? onCancelRun : onClose,
        cancelTitle: formState.isRunning ? 'Stop' : 'Close',
        isLoading: false,
        submitLoading: formState.isRunning,
        isSubmitHidden: formState.isRunning,
        isSubmitDisabled:
          pendingProducts.length === 0 ||
          (!formState.includePrintImages && !formState.includeProductImages),
        submitTitle: isFinishedWithFailures
          ? `Retry ${singularPluralFormat(
              pendingProducts.length,
              'product',
              'products'
            )}`
          : `Regenerate ${singularPluralFormat(
              pendingProducts.length,
              'product',
              'products'
            )}`,
        onSubmit: onStart
      }}
      isOpen={isOpen}
      noCloseButton={formState.isRunning}
      onClose={formState.isRunning ? () => {} : onClose}
      size={700}
      title="Regenerate images for this design"
    >
      <AppStack style={{ gap: 20 }}>
        <AppStack style={{ gap: 10 }}>
          <AppText style={{ fontSize: 14 }}>
            This regenerates the images for every product attached to this
            design, one product at a time.
          </AppText>

          <AppText style={{ fontSize: 14, color: '#666', fontStyle: 'italic' }}>
            This operation populates all design template sizes, retrieves any
            new images, and automatically aligns each variant with its
            corresponding image. It can take a while for designs with many
            products — leave this window open until it finishes.
          </AppText>
        </AppStack>

        {!formState.hasRun && (
          <>
            <Divider label="Regenerate options" />

            <AppText
              style={{
                fontSize: 14,
                color: '#666',
                fontStyle: 'italic',
                textAlign: 'center'
              }}
            >
              Each selection will increase the time it takes to complete the
              regeneration process.
            </AppText>

            <AppCheckbox
              checked={formState.includePrintImages}
              description="This will regenerate the print images used for production and mockups."
              disabled={formState.isRunning}
              label="Print images"
              onChange={() =>
                setFormState((c) => ({
                  ...c,
                  includePrintImages: !c.includePrintImages
                }))
              }
              styles={{ label: { fontWeight: 500 } }}
            />
            <AppCheckbox
              checked={formState.includeProductImages}
              description="This will regenerate the product images used for display and marketing."
              disabled={formState.isRunning}
              label="Product images"
              onChange={() =>
                setFormState((c) => ({
                  ...c,
                  includeProductImages: !c.includeProductImages
                }))
              }
              styles={{ label: { fontWeight: 500 } }}
            />
          </>
        )}

        {formState.hasRun && (
          <AppStack style={{ gap: 10 }}>
            <Divider label="Progress" />

            <AppFlexbox
              style={{ justifyContent: 'space-between', alignItems: 'center' }}
            >
              <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                {formState.completedCount} of {activeBatch.length} completed
              </AppText>
              <AppText style={{ fontSize: 14, color: '#666' }}>
                {formState.failed.length > 0
                  ? `${formState.failed.length} failed`
                  : ''}
              </AppText>
            </AppFlexbox>

            <Progress
              animated={formState.isRunning}
              color={formState.failed.length > 0 ? 'orange' : 'dark'}
              radius="md"
              size="lg"
              value={progressPercent}
            />

            {formState.isRunning && (
              <AppText
                style={{ fontSize: 13, color: '#666', fontStyle: 'italic' }}
              >
                Regenerating {currentProductName}…
              </AppText>
            )}
          </AppStack>
        )}

        {isFinishedWithFailures && (
          <Alert
            color="red"
            icon={<AlertCircle />}
            title={`${singularPluralFormat(
              formState.failed.length,
              'product',
              'products'
            )} failed`}
            variant="outline"
          >
            <AppStack style={{ gap: 8 }}>
              <AppText style={{ fontSize: 14 }}>
                No images were changed for these products. Retry them, or open a
                product to regenerate it on its own.
              </AppText>
              <AppStack style={{ gap: 6 }}>
                {formState.failed.map((f) => (
                  <AppStack key={f.pkEcomVendorProduct} style={{ gap: 0 }}>
                    <AppText style={{ fontSize: 13, fontWeight: 500 }}>
                      {f.name}
                    </AppText>
                    <AppText style={{ fontSize: 12, color: '#666' }}>
                      {f.error}
                    </AppText>
                  </AppStack>
                ))}
              </AppStack>
            </AppStack>
          </Alert>
        )}
      </AppStack>
    </ResponsiveModal>
  );
};

RegenerateDesignImagesModal.propTypes = {
  fkEcomVendor: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSuccess: PropTypes.func,
  products: PropTypes.array
};

export default RegenerateDesignImagesModal;
